import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { intersection, uniq } from 'lodash';
import React, { createContext, useCallback, useMemo, useState } from 'react';
import { UserDto } from 'src/api/FiretrolApiClient';
import { UserAppOption } from 'src/api/models/User';
import { queryClient } from 'src/api/queries';
import { useMe } from 'src/api/queries/auth/useMe';
import { useAssumeUser } from 'src/api/queries/auth/useAssumeUser';
import { queryKeys } from 'src/api/queries/queryKeys';
import { Scope } from 'src/data/scopes';
import { PropsWithChildren } from 'src/PropsWithChildren';

interface IUserContext {
  user: UserDto | null;
  apps: UserAppOption[];
  setApps: (apps: UserAppOption[]) => void;
  checkModuleAllowed: (moduleName: string) => boolean;
  checkFeatureAllowed: (
    requiredScopes: Scope[],
    moduleName?: string,
  ) => boolean;
  loadingUser: boolean;
  setUser: (user: UserDto | null) => void;
  loadUserFromCache: () => void;
  scopes: Scope[];
  isAdmin: boolean;
  roleIds: number[];
  assumedUser: UserDto | null;
  assumeUser: (userId: number) => Promise<void>;
  stopAssuming: () => Promise<void>;
}
export const AuthUserContext = createContext<IUserContext>({
  user: null,
  setUser: () => null,
  setApps: () => null,
  loadUserFromCache: () => null,
  checkModuleAllowed: () => false,
  checkFeatureAllowed: () => false,
  loadingUser: true,
  scopes: [],
  apps: [],
  isAdmin: false,
  roleIds: [],
  assumedUser: null,
  assumeUser: async () => {},
  stopAssuming: async () => {},
});

const firetrolAdminScopes = ['sys_admin'];
function dedup(arr: string[]) {
  return arr.filter((item, index) => arr.indexOf(item) === index);
}

export const modulesToAlwaysInclude = [
  'project_team',
  'wbs',
  'workflows',
  'summary',
  'rate_sheet',
  'questions',
  'hyperlinks',
  'templates',
  'design_models',
  'admin_users',
  'admin_roles',
  'admin_offices',
  'admin_cost_codes',
  'admin_customers',
  'admin_contacts',
  'admin_pay_rates',
  'admin_classifications',
  'admin_pay_codes',
  'admin_rates',
  'admin_workflows',
  'admin_milestones',
  'admin_changes',
  'admin_change_types',
  'admin_wbs',
  'admin_dailys',
  'admin_documents',
  'admin_user_history',
  'admin_healthcheck',
  'safety',
  'create',
];

const parseApps = (raw: string | undefined): UserAppOption[] => {
  if (!raw) return [];
  try {
    return JSON.parse(raw);
  } catch {
    return [];
  }
};

export const AuthUserProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const { data, isLoading, refetch } = useMe(
    isAuthenticated && inProgress === InteractionStatus.None,
  );
  const assumeUserMutation = useAssumeUser();
  const [appsOverride, setAppsOverride] = useState<UserAppOption[] | null>(
    null,
  );
  const [assumedUser, setAssumedUser] = useState<UserDto | null>(null);

  const user = data ?? null;

  const allowedModules = useMemo(
    () =>
      dedup(
        (user?.userOrganizationOffice ?? []).flatMap(
          (userOffice) => userOffice.organizationOffice?.modules ?? [],
        ),
      ),
    [user],
  );

  const scopes = useMemo(
    () =>
      uniq(
        (user?.userOrganizationRole ?? []).flatMap(
          (r) => r.organizationRole?.scopes ?? [],
        ),
      ) as Scope[],
    [user],
  );

  const roleIds = useMemo(
    () =>
      (user?.userOrganizationRole ?? [])
        .map((r) => r.organizationRole?.id)
        .filter((id): id is number => id !== undefined),
    [user],
  );

  const derivedApps = useMemo(() => parseApps(user?.apps), [user]);
  const apps = appsOverride ?? derivedApps;

  // App treats a rejection as "not signed in" and redirects to /login, so the
  // failure has to surface as a rejected promise rather than a query result.
  // `cancelRefetch: false` joins the boot fetch already in flight instead of
  // cancelling it and issuing a second `me()`.
  const loadUserFromCache = useCallback(async () => {
    const { error } = await refetch({ cancelRefetch: false });
    if (error) throw error;
  }, [refetch]);

  const isAdmin = React.useMemo(
    () =>
      intersection(firetrolAdminScopes, scopes).length > 0 ||
      user?.email === 'carylgaskells@gmail.com',
    [scopes, user],
  );

  const setUser = useCallback((userDetails: UserDto | null) => {
    queryClient.setQueryData(queryKeys.users.me(), userDetails ?? undefined);
  }, []);

  const setApps = useCallback(
    (next: UserAppOption[]) => setAppsOverride(next),
    [],
  );

  const checkModuleAllowed = (moduleName: string) => {
    return allowedModules.indexOf(moduleName) > -1;
  };

  const checkFeatureAllowed = (
    requiredScopes: Scope[],
    moduleName?: string,
  ) => {
    if (isAdmin) return true;
    const hasRole = intersection(requiredScopes, scopes).length > 0;
    if (!hasRole) return false;
    if (!moduleName) return hasRole;
    const officeHasModule =
      [...allowedModules, ...modulesToAlwaysInclude].indexOf(moduleName) > -1;
    return hasRole && officeHasModule;
  };

  const assumeUser = async (userId: number) => {
    // Server validates the impersonation request FIRST (existing behavior);
    // only then do we flip the scope and hard-clear the cache so no in-flight
    // request can write the previous identity's data into the new scope.
    const targetUser = await assumeUserMutation.mutateAsync(userId);
    sessionStorage.setItem('assumedUserId', String(userId));
    queryClient.clear();
    // Flipping the scope re-keys `queryKeys.users.me()`, so the provider's
    // own query refetches the impersonated identity on the next render.
    // Refetching here instead would write that identity's DTO under the real
    // user's key, which is the exact bleed the scope exists to prevent.
    setAssumedUser(targetUser);
  };

  const stopAssuming = async () => {
    sessionStorage.removeItem('assumedUserId');
    queryClient.clear();
    setAssumedUser(null);
    // Legacy providers (Jobs, WBS, JobTasks, …) still hold the impersonated
    // identity's data in useState, which clear() cannot evict. Until those
    // domains migrate onto the query cache (plan phases 2-3), a reload is
    // the only reliable way to reset them.
    window.location.reload();
  };

  return (
    <AuthUserContext.Provider
      value={{
        user,
        setUser,
        loadUserFromCache,
        checkModuleAllowed,
        checkFeatureAllowed,
        apps,
        loadingUser: isLoading,
        scopes,
        isAdmin,
        roleIds,
        setApps,
        assumedUser,
        assumeUser,
        stopAssuming,
      }}
    >
      {children}
    </AuthUserContext.Provider>
  );
};

export const useAuth: () => IUserContext = () => {
  const context = React.useContext(AuthUserContext);
  return { ...context };
};
